import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CreditMeter, TrialCountdownRibbon } from "@/components/BillingWidgets";
import Nav from "@/components/Nav";
import { Plus, BookOpen, Trash2, ArrowRight, Users, Lock, StickyNote,
          LayoutGrid, List, CheckCircle2, Circle } from "lucide-react";

const VIEW_KEY = "bf360.library.view";     // "grid" | "list"
const SORT_KEY = "bf360.library.sort";     // "updated" | "created" | "az" | "za" | "ready"

function sortBooks(books, mode) {
  const arr = [...(books || [])];
  const t = (b) => b.updated_at || b.created_at || "";
  const c = (b) => b.created_at || b.updated_at || "";
  switch (mode) {
    case "created": arr.sort((a, b) => (c(b) > c(a) ? 1 : c(b) < c(a) ? -1 : 0)); break;
    case "az":       arr.sort((a, b) => (a.title || "").localeCompare(b.title || "")); break;
    case "za":       arr.sort((a, b) => (b.title || "").localeCompare(a.title || "")); break;
    case "ready":    arr.sort((a, b) => (
      Number(!!b.is_ready_to_ship) - Number(!!a.is_ready_to_ship) ||
      (t(b) > t(a) ? 1 : t(b) < t(a) ? -1 : 0)
    )); break;
    case "updated":
    default:         arr.sort((a, b) => (t(b) > t(a) ? 1 : t(b) < t(a) ? -1 : 0));
  }
  return arr;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  // iter-103 — new-book menu (Blank / From template) + template picker.
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [showFromTemplate, setShowFromTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [movingId, setMovingId] = useState(null);
  // iter-84 — persisted view mode + sort so the author's preference
  // survives reloads and switching between books.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) || "grid"; } catch { return "grid"; }
  });
  const [sortMode, setSortMode] = useState(() => {
    try { return localStorage.getItem(SORT_KEY) || "updated"; } catch { return "updated"; }
  });
  // iter-88 — "Ready to ship only" filter chip. Persisted so the
  // author's launch-review view survives reloads.
  const [readyOnly, setReadyOnly] = useState(() => {
    try { return localStorage.getItem("bf360.library.ready_only") === "1"; } catch { return false; }
  });
  // iter-104/105 — Workspace scope filter. Encodes as:
  //   ""         → All my books (default)
  //   "personal" → Books NOT in any workspace (only me)
  //   "<ws_id>"  → Books in that specific workspace
  //
  // Initial value comes from ``?workspace=<id>`` in the URL if present
  // (iter-105 deep-link) so authors can bookmark a workspace view.
  // Falls back to the last choice saved in localStorage. When the
  // filter changes, the URL is updated in-place with
  // ``history.replaceState`` so refresh + share both work.
  const [searchParams, setSearchParams] = useSearchParams();
  const [wsFilter, setWsFilter] = useState(() => {
    const fromUrl = (searchParams.get("workspace") || "").trim();
    if (fromUrl) return fromUrl;
    try { return localStorage.getItem("bf360.library.ws_filter") || ""; } catch { return ""; }
  });
  useEffect(() => { try { localStorage.setItem(VIEW_KEY, viewMode); } catch {} }, [viewMode]);
  useEffect(() => { try { localStorage.setItem(SORT_KEY, sortMode); } catch {} }, [sortMode]);
  useEffect(() => {
    try { localStorage.setItem("bf360.library.ready_only", readyOnly ? "1" : "0"); } catch {}
  }, [readyOnly]);
  useEffect(() => {
    try { localStorage.setItem("bf360.library.ws_filter", wsFilter); } catch {}
    // iter-105 — mirror the filter into the URL query so authors can
    // bookmark or share a workspace view. Empty filter drops the param.
    const next = new URLSearchParams(searchParams);
    if (wsFilter) next.set("workspace", wsFilter);
    else next.delete("workspace");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [wsFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  // iter-64 — new book can now land directly in a workspace so
  // teammates see it immediately (no admin-only two-step move).
  const [newWorkspaceId, setNewWorkspaceId] = useState("");
  // iter-104 — when the library is scoped to a specific workspace,
  // pre-select that workspace on the "New book" form so authors don't
  // have to re-pick it every time.
  useEffect(() => {
    if (wsFilter && wsFilter !== "personal") setNewWorkspaceId(wsFilter);
    else if (wsFilter === "personal") setNewWorkspaceId("");
  }, [wsFilter]);
  const nav = useNavigate();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const sortedBooks = React.useMemo(() => {
    let sorted = sortBooks(books, sortMode);
    if (readyOnly) sorted = sorted.filter((b) => !!b.is_ready_to_ship);
    // iter-104/105 — apply the workspace scope filter last. Books now
    // carry a plural `workspace_ids` list (iter-105) so we match on
    // either the legacy singular or plural field.
    const inWs = (b, wid) =>
      (Array.isArray(b.workspace_ids) && b.workspace_ids.includes(wid))
      || b.workspace_id === wid;
    if (wsFilter === "personal") {
      sorted = sorted.filter((b) => !b.workspace_id && !(b.workspace_ids || []).length);
    } else if (wsFilter) {
      sorted = sorted.filter((b) => inWs(b, wsFilter));
    }
    return sorted;
  }, [books, sortMode, readyOnly, wsFilter]);

  const toggleReady = async (bookId, next) => {
    // Optimistic — flip locally, roll back on error
    setBooks((prev) => prev.map((b) => b.id === bookId ? { ...b, is_ready_to_ship: next } : b));
    try {
      await api.patch(`/books/${bookId}`, { is_ready_to_ship: next });
      toast.success(next ? "Marked ready to ship" : "Ready flag cleared");
    } catch (err) {
      setBooks((prev) => prev.map((b) => b.id === bookId ? { ...b, is_ready_to_ship: !next } : b));
      toast.error(formatApiError(err));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/books");
      setBooks(data);
      // iter-64 — every user needs their workspace list so members can
      // both PICK a workspace on create AND see the "shared workspace"
      // badge on cards that already live inside one. Previously this
      // was admin-only, which is why non-admin members never saw the
      // sharing controls.
      // iter-108 — super-admins additionally pull EVERY workspace so
      // they can drop a reference/sample book into any team's library
      // (VAs in different teams see the shared book but never each
      // other). Non-admin members still only see their own workspaces.
      try {
        const path = isAdmin ? "/workspaces/all" : "/workspaces/mine";
        const { data: ws } = await api.get(path);
        setWorkspaces(ws || []);
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // iter-103 — lazy-load Book Templates when the picker opens.
  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const { data } = await api.get("/book-templates");
      setTemplates(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setTemplatesLoading(false); }
  };
  useEffect(() => {
    if (showFromTemplate && templates.length === 0) loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFromTemplate]);
  const spawnFromTemplate = async (tid) => {
    try {
      const { data } = await api.post(`/book-templates/${tid}/spawn`);
      toast.success("Book created from template");
      setShowFromTemplate(false);
      nav(`/book/${data.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { title, author };
      if (newWorkspaceId) payload.workspace_id = newWorkspaceId;
      const { data } = await api.post("/books", payload);
      toast.success(newWorkspaceId ? "Book created in workspace" : "Book created");
      setShowNew(false);
      setTitle(""); setAuthor(""); setNewWorkspaceId("");
      nav(`/book/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this book?")) return;
    try {
      await api.delete(`/books/${id}`);
      setBooks(books.filter((b) => b.id !== id));
      toast.success("Book deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const moveToWorkspace = async (bookId, wsId) => {
    setMovingId(bookId);
    try {
      const { data } = await api.patch(`/books/${bookId}/workspace`,
                                        { workspace_id: wsId || "" });
      setBooks((prev) => prev.map((b) => b.id === bookId ? { ...b, ...data } : b));
      toast.success(wsId ? "Moved into workspace" : "Removed from workspace");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setMovingId(null); }
  };

  // iter-106 — multi-workspace assignment. `workspace_ids` is the
  // source of truth on the backend (iter-105). This is the write
  // path used by the checkbox popover on each book card.
  const saveWorkspaceIds = async (bookId, wsIds) => {
    setMovingId(bookId);
    try {
      const { data } = await api.patch(`/books/${bookId}`, { workspace_ids: wsIds });
      setBooks((prev) => prev.map((b) => b.id === bookId ? { ...b, ...data } : b));
      toast.success(wsIds.length
        ? `Shared with ${wsIds.length} workspace${wsIds.length === 1 ? "" : "s"}`
        : "Removed from every workspace");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setMovingId(null); }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <TrialCountdownRibbon/>
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="masthead-rule flex items-center justify-between text-xs uppercase tracking-[0.2em] mb-10">
          <span>Section II</span>
          <span>The Library</span>
          <span>{new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
        </div>

        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">Your works</p>
            <h1 className="font-editorial text-6xl leading-none mt-3" data-testid="dashboard-title">Library</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/library/workspaces" className="btn-ghost inline-flex items-center gap-2" data-testid="workspaces-link">
              Workspaces
            </Link>
            <Link to="/library/preset-templates" className="btn-ghost inline-flex items-center gap-2" data-testid="preset-templates-link">
              Preset Templates
            </Link>
            <Link to="/library/book-templates" className="btn-ghost inline-flex items-center gap-2" data-testid="book-templates-link">
              Book Templates
            </Link>
            <Link to="/ph360" className="btn-ghost inline-flex items-center gap-2" data-testid="ph360-pro-link">
              PH360 Pro →
            </Link>
            <div className="relative" data-testid="new-book-menu-wrap">
              <button className="btn-primary inline-flex items-center gap-2"
                      onClick={() => setShowNewMenu((v)=>!v)}
                      data-testid="new-book-btn">
                <Plus className="w-4 h-4" strokeWidth={1.5}/> New book
              </button>
              {showNewMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-[color:var(--border)] shadow-md z-20"
                     data-testid="new-book-menu">
                  <button className="w-full text-left px-4 py-3 text-sm hover:bg-[color:var(--canvas)]"
                          onClick={()=>{ setShowNewMenu(false); setShowNew(true); }}
                          data-testid="new-book-blank">
                    <p className="font-medium">Blank book</p>
                    <p className="text-[10px] text-[color:var(--text-muted)]">Start from scratch — empty title, no chapters.</p>
                  </button>
                  <button className="w-full text-left px-4 py-3 text-sm border-t border-[color:var(--border)] hover:bg-[color:var(--canvas)]"
                          onClick={()=>{ setShowNewMenu(false); setShowFromTemplate(true); }}
                          data-testid="new-book-from-template">
                    <p className="font-medium">From template →</p>
                    <p className="text-[10px] text-[color:var(--text-muted)]">Reuse a Book Template you've saved — chapters, front / back matter and design carry over.</p>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <CreditMeter/>

        {loading ? (
          <p className="text-[color:var(--text-muted)]">Loading…</p>
        ) : books.length === 0 ? (
          <div className="border border-dashed border-[color:var(--border)] p-16 text-center" data-testid="empty-library">
            <BookOpen className="w-8 h-8 mx-auto mb-4" strokeWidth={1.25} />
            <p className="font-editorial text-3xl">No books yet.</p>
            <p className="text-[color:var(--text-muted)] mt-2 mb-6">Create your first book to begin.</p>
            <button className="btn-primary inline-flex items-center gap-2" onClick={()=>setShowNew(true)} data-testid="empty-new-book">
              <Plus className="w-4 h-4" strokeWidth={1.5}/> Create a book
            </button>
          </div>
        ) : (
          <>
            {/* iter-84 — View + Sort toolbar */}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4"
                  data-testid="library-toolbar">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[11px] uppercase tracking-widest text-[color:var(--text-muted)]">
                  {sortedBooks.length} book{sortedBooks.length === 1 ? "" : "s"}
                </span>
                {/* iter-88 — Ready to ship only filter chip */}
                <button onClick={() => setReadyOnly((v) => !v)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] uppercase tracking-widest border transition-colors ${
                          readyOnly
                            ? "bg-emerald-700 text-white border-emerald-700"
                            : "bg-white text-[color:var(--text-muted)] border-[color:var(--border)] hover:text-black"
                        }`}
                        title={readyOnly ? "Showing only ready-to-ship books" : "Show only ready-to-ship books"}
                        data-testid="library-ready-filter-chip">
                  {readyOnly
                    ? <CheckCircle2 className="w-3 h-3" strokeWidth={2}/>
                    : <Circle className="w-3 h-3" strokeWidth={1.5}/>}
                  Ready to ship only
                </button>
                {/* iter-104 — Workspace scope filter. Lets you pick a
                    single workspace and see only its books, instead
                    of the merged view of every workspace you belong
                    to. Choice is persisted per-browser. */}
                {workspaces.length > 0 && (
                  <select value={wsFilter} onChange={(e)=>setWsFilter(e.target.value)}
                          className="border border-[color:var(--border)] px-2 py-1 text-xs bg-white"
                          title="Filter the library by workspace"
                          data-testid="library-ws-filter">
                    <option value="">All my books ({books.length})</option>
                    <option value="personal">
                      Personal only ({books.filter((b) => !b.workspace_id && !(b.workspace_ids || []).length).length})
                    </option>
                    {workspaces.map((w) => {
                      const n = books.filter((b) =>
                        (Array.isArray(b.workspace_ids) && b.workspace_ids.includes(w.id))
                        || b.workspace_id === w.id
                      ).length;
                      return (
                        <option key={w.id} value={w.id} data-testid={`library-ws-filter-opt-${w.id}`}>
                          Workspace · {w.name} ({n})
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                  Sort by
                </label>
                <select value={sortMode} onChange={(e)=>setSortMode(e.target.value)}
                        className="border border-[color:var(--border)] px-2 py-1 text-xs bg-white"
                        data-testid="library-sort">
                  <option value="updated">Recently edited</option>
                  <option value="created">Date created (newest)</option>
                  <option value="az">Title A–Z</option>
                  <option value="za">Title Z–A</option>
                  <option value="ready">Ready to ship first</option>
                </select>
                <div className="flex border border-[color:var(--border)]">
                  <button onClick={()=>setViewMode("grid")}
                          className={`p-1.5 ${viewMode === "grid"
                                             ? "bg-[color:var(--text)] text-white"
                                             : "text-[color:var(--text-muted)] hover:bg-[color:var(--canvas)]"}`}
                          title="Grid view"
                          data-testid="library-view-grid">
                    <LayoutGrid className="w-4 h-4" strokeWidth={1.5}/>
                  </button>
                  <button onClick={()=>setViewMode("list")}
                          className={`p-1.5 ${viewMode === "list"
                                             ? "bg-[color:var(--text)] text-white"
                                             : "text-[color:var(--text-muted)] hover:bg-[color:var(--canvas)]"}`}
                          title="List view"
                          data-testid="library-view-list">
                    <List className="w-4 h-4" strokeWidth={1.5}/>
                  </button>
                </div>
              </div>
            </div>
          {sortedBooks.length === 0 && readyOnly ? (
            <div className="border border-dashed border-[color:var(--border)] p-12 text-center"
                  data-testid="library-ready-empty">
              <CheckCircle2 className="w-6 h-6 mx-auto mb-3 text-[color:var(--text-muted)]" strokeWidth={1.25}/>
              <p className="font-editorial text-2xl">No books marked ready yet.</p>
              <p className="text-[color:var(--text-muted)] text-sm mt-2">
                Toggle a book's ready badge from the card, or clear the filter to see everything.
              </p>
              <button onClick={()=>setReadyOnly(false)}
                      className="btn-ghost text-xs mt-4 inline-flex items-center gap-1"
                      data-testid="library-ready-empty-clear">
                Clear filter
              </button>
            </div>
          ) : viewMode === "list" ? (
            <div className="border border-[color:var(--border)] bg-white overflow-x-auto"
                  data-testid="library-list-view">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] border-b border-[color:var(--border)]">
                    <th className="py-2 px-3 w-8"></th>
                    <th className="py-2 px-3">Title</th>
                    <th className="py-2 px-3">Author</th>
                    <th className="py-2 px-3">Chapters</th>
                    <th className="py-2 px-3">Workspace</th>
                    <th className="py-2 px-3">Updated</th>
                    <th className="py-2 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                {sortedBooks.map((b) => {
                  const isMine = !b.owner_user_id || b.owner_user_id === user?.id;
                  const bookWs = b.workspace_id
                    ? workspaces.find((w) => w.id === b.workspace_id)
                    : null;
                  const canDelete = bookWs ? bookWs.owner_user_id === user?.id : isMine;
                  return (
                    <tr key={b.id} className="border-b border-[color:var(--border)] hover:bg-[color:var(--canvas)]"
                        data-testid={`book-row-${b.id}`}>
                      <td className="py-2 px-3">
                        <button onClick={()=>toggleReady(b.id, !b.is_ready_to_ship)}
                                className={b.is_ready_to_ship
                                            ? "text-green-700"
                                            : "text-[color:var(--text-muted)] hover:text-[color:var(--text)]"}
                                title={b.is_ready_to_ship ? "Marked ready to ship" : "Mark ready to ship"}
                                data-testid={`book-ready-${b.id}`}>
                          {b.is_ready_to_ship
                            ? <CheckCircle2 className="w-4 h-4" strokeWidth={1.5}/>
                            : <Circle className="w-4 h-4" strokeWidth={1.5}/>}
                        </button>
                      </td>
                      <td className="py-2 px-3">
                        <Link to={`/book/${b.id}`} className="font-medium hover:underline">
                          {b.title || "Untitled"}
                        </Link>
                        {b.is_ready_to_ship && (
                          <span className="ml-2 text-[9px] uppercase tracking-widest px-1 py-0.5 border border-green-700 text-green-700">
                            Ready
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-[color:var(--text-muted)]">{b.author || "—"}</td>
                      <td className="py-2 px-3 text-[color:var(--text-muted)]">{b.chapters?.length || 0}</td>
                      <td className="py-2 px-3 text-[color:var(--text-muted)] text-xs">
                        {b.workspace_id ? (b.workspace_name || "Workspace") : "Personal"}
                      </td>
                      <td className="py-2 px-3 text-[color:var(--text-muted)] text-xs">
                        {(b.updated_at || b.created_at || "").slice(0, 10) || "—"}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <Link to={`/book/${b.id}`}
                              className="text-xs link-underline mr-3"
                              data-testid={`open-book-row-${b.id}`}>Open</Link>
                        {canDelete && (
                          <button onClick={()=>remove(b.id)}
                                  className="text-[color:var(--text-muted)] hover:text-[color:var(--primary)]"
                                  data-testid={`delete-book-row-${b.id}`}>
                            <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[color:var(--border)] border border-[color:var(--border)]">
            {sortedBooks.map((b) => {
              const isMine = !b.owner_user_id || b.owner_user_id === user?.id;
              // iter-68 — workspace book deletion is gated to the workspace
              // owner (admin). Personal books stay owner-only.
              const bookWs = b.workspace_id
                ? workspaces.find((w) => w.id === b.workspace_id)
                : null;
              const canDelete = bookWs
                ? bookWs.owner_user_id === user?.id
                : isMine;
              const summary = b.tags_summary || { count: 0, latest_text: "", latest_nickname: "" };
              return (
              <div key={b.id} className="bg-white p-6 flex flex-col gap-4" data-testid={`book-card-${b.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-20 h-28 bg-[color:var(--canvas)] border border-[color:var(--border)] flex items-center justify-center overflow-hidden shrink-0">
                    {/* iter-91 — thumbnail served as a byte stream so we
                        don't ship the 2 MB base64 blob inside the JSON
                        list. `has_cover` skips the fetch entirely when
                        the book has no cover. */}
                    {b.has_cover ? (
                      <img src={`${API}/books/${b.id}/cover-thumb.jpg`}
                           alt="cover"
                           loading="lazy"
                           className="w-full h-full object-cover"
                           onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                    ) : b.cover_data_url ? (
                      <img src={b.cover_data_url} alt="cover" className="w-full h-full object-cover"/>
                    ) : (
                      <BookOpen className="w-6 h-6 text-[color:var(--text-muted)]" strokeWidth={1.25}/>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-editorial text-2xl leading-tight truncate">{b.title}</h3>
                    <p className="text-sm text-[color:var(--text-muted)] truncate">by {b.author}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                      <span>{b.chapters?.length || 0} chapters</span>
                      {b.workspace_id && (
                        <span className="inline-flex items-center gap-1 border border-[color:var(--primary)] text-[color:var(--primary)] px-1.5 py-0.5"
                              data-testid={`book-card-ws-${b.id}`}>
                          <Users className="w-3 h-3"/> {b.workspace_name || "Workspace"}
                        </span>
                      )}
                      {b.lock && (
                        <span className={"inline-flex items-center gap-1 px-1.5 py-0.5 border " +
                                          (b.lock.is_me ? "border-[color:var(--primary)] text-[color:var(--primary)]"
                                                         : "border-amber-600 text-amber-700")}
                              data-testid={`book-card-lock-${b.id}`}>
                          <Lock className="w-3 h-3"/> {b.lock.is_me ? "You editing" : b.lock.nickname}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {summary.count > 0 && (
                  <div className="border border-[color:var(--border)] bg-[color:var(--canvas)] px-3 py-2 text-[11px]"
                       data-testid={`book-card-notes-${b.id}`}>
                    <div className="flex items-center gap-1 text-[color:var(--text-muted)] uppercase tracking-widest text-[10px]">
                      <StickyNote className="w-3 h-3"/> {summary.count} note{summary.count === 1 ? "" : "s"}
                    </div>
                    <p className="mt-1 line-clamp-1">
                      <span className="text-[color:var(--text-muted)]">{summary.latest_nickname}:</span>{" "}
                      {summary.latest_text}
                    </p>
                  </div>
                )}

                {(isMine || isAdmin) && workspaces.length > 0 && (
                  <WorkspacePickerButton
                    bookId={b.id}
                    // iter-106 — pass the plural list from iter-105
                    // (falls back to legacy singular for pre-105 rows).
                    currentIds={
                      (b.workspace_ids && b.workspace_ids.length)
                        ? b.workspace_ids
                        : (b.workspace_id ? [b.workspace_id] : [])
                    }
                    workspaces={workspaces}
                    disabled={movingId === b.id}
                    onSave={(ids) => saveWorkspaceIds(b.id, ids)}
                  />
                )}

                <div className="flex items-center justify-between rule pt-3">
                  <Link to={`/book/${b.id}`} className="link-underline text-sm inline-flex items-center gap-1" data-testid={`open-book-${b.id}`}>
                    Open <ArrowRight className="w-3 h-3" strokeWidth={1.5}/>
                  </Link>
                  <div className="flex items-center gap-3">
                    <button onClick={()=>toggleReady(b.id, !b.is_ready_to_ship)}
                            className={"inline-flex items-center gap-1 text-[10px] uppercase tracking-widest " +
                                        (b.is_ready_to_ship
                                          ? "text-green-700"
                                          : "text-[color:var(--text-muted)] hover:text-[color:var(--text)]")}
                            title={b.is_ready_to_ship ? "Marked ready to ship" : "Mark ready to ship"}
                            data-testid={`book-ready-${b.id}`}>
                      {b.is_ready_to_ship
                        ? <><CheckCircle2 className="w-3 h-3" strokeWidth={1.5}/>Ready</>
                        : <><Circle className="w-3 h-3" strokeWidth={1.5}/>Mark ready</>}
                    </button>
                    {canDelete && (
                      <button onClick={()=>remove(b.id)} className="text-[color:var(--text-muted)] hover:text-[color:var(--primary)]" data-testid={`delete-book-${b.id}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          )}
          </>
        )}

        {showNew && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={()=>setShowNew(false)}>
            <div className="bg-white border border-[color:var(--border)] w-full max-w-md p-8" onClick={(e)=>e.stopPropagation()} data-testid="new-book-modal">
              <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)] mb-2">§ New title</p>
              <h3 className="font-editorial text-3xl mb-6">Create a book</h3>
              <form onSubmit={create} className="space-y-4">
                <label className="block">
                  <span className="block text-xs uppercase tracking-widest mb-1">Title</span>
                  <input required value={title} onChange={(e)=>setTitle(e.target.value)}
                    className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]" data-testid="new-book-title"/>
                </label>
                <label className="block">
                  <span className="block text-xs uppercase tracking-widest mb-1">Author</span>
                  <input required value={author} onChange={(e)=>setAuthor(e.target.value)}
                    className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]" data-testid="new-book-author"/>
                </label>
                {workspaces.length > 0 && (
                  <label className="block" data-testid="new-book-ws-wrap">
                    <span className="block text-xs uppercase tracking-widest mb-1">Where should this book live?</span>
                    <select
                      value={newWorkspaceId}
                      onChange={(e)=>setNewWorkspaceId(e.target.value)}
                      className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]"
                      data-testid="new-book-ws-select"
                    >
                      <option value="">My personal library (only me)</option>
                      {workspaces.map((w) => (
                        <option key={w.id} value={w.id}>Workspace: {w.name} · shared with team</option>
                      ))}
                    </select>
                    <span className="block text-[11px] text-[color:var(--text-muted)] mt-1">
                      Books added to a workspace are visible to every teammate immediately.
                    </span>
                  </label>
                )}
                <div className="flex gap-3 pt-2">
                  <button type="button" className="btn-ghost flex-1" onClick={()=>setShowNew(false)} data-testid="new-book-cancel">Cancel</button>
                  <button type="submit" disabled={creating} className="btn-primary flex-1" data-testid="new-book-submit">
                    {creating ? "Creating…" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* iter-103 — From-template picker */}
        {showFromTemplate && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
               onClick={()=>setShowFromTemplate(false)}
               data-testid="from-template-modal">
            <div className="bg-white max-w-lg w-full max-h-[80vh] overflow-auto border border-[color:var(--border)]"
                 onClick={(e)=>e.stopPropagation()}>
              <div className="px-6 py-4 border-b border-[color:var(--border)] flex items-center justify-between">
                <h3 className="font-editorial text-xl">New book from template</h3>
                <button onClick={()=>setShowFromTemplate(false)} className="text-[color:var(--text-muted)] hover:text-black" data-testid="from-template-x">×</button>
              </div>
              <div className="px-6 py-4">
                <p className="text-[11px] text-[color:var(--text-muted)] mb-3">
                  Pick a Book Template to clone. Chapters, front / back matter and design controls carry over — cover art starts fresh so you can upload your own.
                </p>
                {templatesLoading ? (
                  <p className="text-sm text-[color:var(--text-muted)]">Loading…</p>
                ) : templates.length === 0 ? (
                  <div className="border border-dashed border-[color:var(--border)] p-6 text-center">
                    <p className="text-sm text-[color:var(--text-muted)] mb-2">No Book Templates yet.</p>
                    <Link to="/library/book-templates" className="btn-ghost text-xs inline-flex items-center gap-1"
                          onClick={()=>setShowFromTemplate(false)}
                          data-testid="from-template-empty-link">
                      Create your first template →
                    </Link>
                  </div>
                ) : (
                  <ul className="divide-y divide-[color:var(--border)]" data-testid="from-template-list">
                    {templates.map((t) => (
                      <li key={t.id} className="py-2">
                        <button className="w-full text-left hover:bg-[color:var(--canvas)] px-2 py-2"
                                onClick={()=>spawnFromTemplate(t.id)}
                                data-testid={`from-template-pick-${t.id}`}>
                          <p className="text-sm font-medium">{t.title || "Untitled template"}</p>
                          <p className="text-[10px] text-[color:var(--text-muted)]">
                            {(t.chapters?.length || 0)} chapters
                            {t.author ? ` · ${t.author}` : ""}
                          </p>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// WorkspacePickerButton (iter-106) — compact multi-workspace picker.
// -----------------------------------------------------------------------------
// A button showing the current assignment ("Personal", "Studio A", or
// "3 workspaces") that opens a popover with a checkbox list. Ticking
// or unticking any workspace is buffered locally; clicking "Save"
// commits the full list via `onSave` (which calls
// PATCH /api/books/{id} with `workspace_ids`).
// Behaves gracefully on mobile — popover uses fixed positioning +
// backdrop dismissal.
// iter-107 — exported so `/library/book-templates` can reuse the
// exact same UI (Book Templates share the books collection and
// `workspace_ids` field, so the picker is a drop-in fit).
export function WorkspacePickerButton({ bookId, currentIds, workspaces, disabled, onSave, itemLabel = "book" }) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(() => new Set(currentIds || []));
  // Re-seed when the parent refreshes (e.g. after save).
  React.useEffect(() => {
    if (!open) setDraft(new Set(currentIds || []));
  }, [currentIds, open]);

  const toggle = (wid) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(wid)) next.delete(wid); else next.add(wid);
      return next;
    });
  };
  const labelText = () => {
    if (!currentIds || currentIds.length === 0) return "Personal (not shared)";
    if (currentIds.length === 1) {
      const w = workspaces.find((x) => x.id === currentIds[0]);
      return w ? `Workspace: ${w.name}` : "1 workspace";
    }
    return `${currentIds.length} workspaces`;
  };
  const dirty = React.useMemo(() => {
    const a = new Set(currentIds || []);
    if (a.size !== draft.size) return true;
    for (const id of a) if (!draft.has(id)) return true;
    return false;
  }, [draft, currentIds]);

  return (
    <>
      <button type="button"
              disabled={disabled}
              onClick={() => setOpen(true)}
              className="text-[11px] border border-[color:var(--border)] px-2 py-1 w-full text-left hover:border-[color:var(--primary)] disabled:opacity-50"
              data-testid={`book-ws-picker-${bookId}`}
              aria-label="Assign workspaces">
        <span className="inline-flex items-center justify-between w-full gap-2">
          <span className="truncate">{labelText()}</span>
          <span className="text-[color:var(--text-muted)]">▾</span>
        </span>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setOpen(false)}
             data-testid={`book-ws-picker-${bookId}-backdrop`}>
          <div className="bg-white max-w-sm w-full max-h-[80vh] flex flex-col border border-[color:var(--border)]"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-[color:var(--border)] flex items-center justify-between">
              <p className="font-editorial text-base">Share {itemLabel} with…</p>
              <button onClick={() => setOpen(false)}
                      className="text-[color:var(--text-muted)] hover:text-black"
                      data-testid={`book-ws-picker-${bookId}-close`}>×</button>
            </div>
            <div className="p-3 overflow-auto flex-1">
              <p className="text-[10px] text-[color:var(--text-muted)] mb-2">
                Tick every workspace this {itemLabel} should live in. Untick to remove.
              </p>
              <div className="border border-[color:var(--border)] divide-y divide-[color:var(--border)]"
                   data-testid={`book-ws-picker-${bookId}-list`}>
                {workspaces.map((w) => {
                  const checked = draft.has(w.id);
                  return (
                    <label key={w.id}
                           className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-[color:var(--canvas)]"
                           data-testid={`book-ws-picker-${bookId}-opt-${w.id}`}>
                      <input type="checkbox" checked={checked}
                             onChange={() => toggle(w.id)}
                             data-testid={`book-ws-picker-${bookId}-cb-${w.id}`}/>
                      <span className="text-xs truncate">{w.name || "Workspace"}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-[color:var(--text-muted)] mt-2">
                {draft.size === 0
                  ? `Personal — only you can see this ${itemLabel}.`
                  : `Shared with ${draft.size} workspace${draft.size === 1 ? "" : "s"}.`}
              </p>
            </div>
            <div className="border-t border-[color:var(--border)] px-4 py-3 flex items-center justify-end gap-2">
              <button onClick={() => { setDraft(new Set(currentIds || [])); setOpen(false); }}
                      className="btn-ghost text-xs"
                      data-testid={`book-ws-picker-${bookId}-cancel`}>
                Cancel
              </button>
              <button onClick={async () => {
                        await onSave(Array.from(draft));
                        setOpen(false);
                      }}
                      disabled={!dirty || disabled}
                      className="btn-primary text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`book-ws-picker-${bookId}-save`}>
                {dirty ? "Save" : "No changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
